import { computed, ref, watch } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useAlertsStore } from '@/stores/alerts.store'
import { useUsersStore } from '@/stores/users.store'
import { useProjectStore } from '@/stores/projects.store'
import { useLocationStore } from '@/stores/location.store'
import { useHubStore } from '@/stores/hubs.store'
import { useModuleStore } from '@/stores/modules.store'
import { useComappStore } from '@/stores/comapps.store'
import { useBaseStore } from '@/stores/base.store'
import { diff, dotify } from '@/utils/tools'
import type {
	AlertMatrixSelection,
	AlertMetaSelection,
	AlertSignalState,
	AlertsHierarchyNode,
	CustomGraph,
	CustomGraphEquation,
	SensorAlertRow,
} from './_types'

type InitOptions = {
	force?: boolean
	includeStates?: boolean
}

interface FlattenedEquation {
	key: string
	graphId: string
	graphSerial: string
	equationSerial: string
	graphName: string
	equationName: string
	projectId: string
	locationId: string
	isOnAlert?: boolean
	isOnWarn?: boolean
	graph: CustomGraph
	equation: CustomGraphEquation
}

export type { FlattenedEquation }

export interface UserMatrixContext {
	selectedProjectIds: Ref<string[]>
	hasWarnAlerts: Ref<boolean>
	warnSelectAll: Ref<Record<string, boolean>>
	alertSelectAll: Ref<Record<string, boolean>>
	userAlertData: Ref<AlertMatrixSelection>
	currentUserAlertData: Ref<AlertMatrixSelection>
	alertMetaData: Ref<AlertMetaSelection>
	currentUserAlertMetaData: Ref<AlertMetaSelection>
	filteredEquations: ComputedRef<Record<string, FlattenedEquation>>
	hasChanges: ComputedRef<boolean>
	diffs: ComputedRef<ReturnType<typeof diff>>
	diffsMeta: ComputedRef<ReturnType<typeof diff>>
	prepareAlertData: () => void
	computeAccessMatrix: () => void
	syncFromStores: () => void
	setSelectAll: (type: 'warn' | 'alert', key: string, value: boolean) => void
	resetSelectAll: () => void
	hasAccessToEquation: (userId: string, graphSerial: string, projectId: string) => boolean
	hasUserAccessToProject: (user: any) => boolean
}

export interface GraphMatrixContext {
	includeWarnAlerts: Ref<boolean>
	/** '' = all projects; otherwise a project id (or 'general'). */
	selectedProjectId: Ref<string>
	equationsByProject: ComputedRef<Record<string, FlattenedEquation[]>>
	graphAlertDataByEquationKey: ComputedRef<Record<string, AlertSignalState>>
}

let initInFlight: Promise<void> | null = null
let statesLoadedOnce = false

const hasStatePayload = (value: any): boolean => {
	if (!value) return false
	const projects = value.projects || {}
	return Object.keys(projects).length > 0
}

const ADMIN_ROLES = new Set(['admin', 'developer'])

const makeProjectOptionLabel = (project: any) => {
	const number = project?.projectNumber ? `${project.projectNumber} ` : ''
	const name = project?.name || 'Untitled Project'
	return `${number}${name}`.trim()
}

export function useAlerts() {
	const alertsStore = useAlertsStore()
	const usersStore = useUsersStore()
	const projectStore = useProjectStore()
	const locationStore = useLocationStore()
	const hubStore = useHubStore()
	const moduleStore = useModuleStore()
	const comappStore = useComappStore()
	const baseStore = useBaseStore()

	const { customGraphs, alerts, warns, loadingMap: alertsLoading, equationIndex } = storeToRefs(alertsStore)
	const { users, loading: userLoading } = storeToRefs(usersStore)
	const { projects, projectMap } = storeToRefs(projectStore)
	const { locationsMap, locationsByProjectId } = storeToRefs(locationStore)
	const { hubsByLoc } = storeToRefs(hubStore)
	const { modules, modulesByHubSerial } = storeToRefs(moduleStore)
	const { comappsByLId } = storeToRefs(comappStore)
	const { allStates } = storeToRefs(baseStore)

	if (!statesLoadedOnce && hasStatePayload(allStates.value)) {
		statesLoadedOnce = true
	}

	const combinedLoading = computed(() => {
		const alertLoading = Object.keys(alertsLoading.value).length > 0
		const userFetching = Object.values(userLoading.value).some(Boolean)
		return alertLoading || userFetching || Boolean(initInFlight)
	})

	const projectOptions = computed(() => {
		const list = [...projects.value]
		list.sort((a, b) => {
			const numA = a?.projectNumber || ''
			const numB = b?.projectNumber || ''
			return numA.localeCompare(numB)
		})
		return [
			{ label: 'General', value: 'general' },
			...list.map((project) => ({
				label: makeProjectOptionLabel(project),
				value: project?._id,
				shortName: project?.shortName,
			})),
		]
	})

	const graphMetadata = computed(() => {
		const eqIndex = equationIndex.value || {}
		const locMap = locationsMap.value || {}
		const equationMap: Record<string, FlattenedEquation> = {}
		const projectSerialSets: Record<string, Set<string>> = {}
		const allSerialSet = new Set<string>()

		const normalizeProjectId = (graph: CustomGraph) => {
			const projectId = graph.projectId || locMap[graph.locationId]?.projectId || ''
			return projectId || 'general'
		}

		for (const [key, entry] of Object.entries(eqIndex)) {
			const { graph, equation } = entry
			const projectId = normalizeProjectId(graph)

			equationMap[key] = {
				key,
				graphId: graph._id,
				graphSerial: graph.serial,
				equationSerial: equation.serial,
				graphName: graph.name,
				equationName: equation.name,
				projectId,
				locationId: graph.locationId,
				isOnAlert: equation.isOnAlert,
				isOnWarn: equation.isOnWarn,
				graph,
				equation,
			}

			if (!projectSerialSets[projectId]) projectSerialSets[projectId] = new Set<string>()
			projectSerialSets[projectId].add(graph.serial)
			allSerialSet.add(graph.serial)
		}

		const graphSerialsByProject: Record<string, string[]> = {}
		for (const [projectId, serials] of Object.entries(projectSerialSets)) {
			graphSerialsByProject[projectId] = Array.from(serials)
		}

		return {
			equations: equationMap,
			graphSerialsByProject,
			allGraphSerials: Array.from(allSerialSet),
		}
	})

	const flattenedEquations = computed<Record<string, FlattenedEquation>>(
		() => graphMetadata.value.equations,
	)

	const graphSerialsByProject = computed<Record<string, string[]>>(
		() => graphMetadata.value.graphSerialsByProject,
	)

	const allGraphSerials = computed<string[]>(() => graphMetadata.value.allGraphSerials)

	const graphAlertDataByEquationKey = computed<Record<string, AlertSignalState>>(() => {
		const eqIndex = equationIndex.value || {}
		const map: Record<string, AlertSignalState> = {}
		for (const [key, entry] of Object.entries(eqIndex)) {
			const { graph, equation } = entry
			map[key] = graph.alertData?.[equation.serial] || {}
		}
		return map
	})

	const sensorAlerts = computed<SensorAlertRow[]>(() => {
		const result: SensorAlertRow[] = []
		const alertsMap = alerts.value || {}
		const warnsMap = warns.value || {}
		const graphsById = alertsStore.customGraphsById
		const locMap = locationsMap.value
		const projMap = projectMap.value

		const graphIds = new Set([...Object.keys(alertsMap), ...Object.keys(warnsMap)])
		for (const cgId of graphIds) {
			const graph = graphsById[cgId]
			if (!graph) continue
			for (const eq of graph.equations || []) {
				const skey = `-/-/${eq.name}`
				const alertEntry = alertsMap[cgId]
				const warnEntry = warnsMap[cgId]
				const isAlert = Boolean(alertEntry?.data?.[skey])
				const isWarn = Boolean(warnEntry?.data?.[skey])
				if (!isAlert && !isWarn) continue
				const lastAlertDate = alertEntry?.alertDates?.[skey]
				const lastWarnDate = warnEntry?.warnDates?.[skey]
				result.push({
					key: `${cgId}//${eq.serial}`,
					cgId,
					cgName: graph.name,
					locationId: graph.locationId,
					locationName: locMap[graph.locationId]?.name,
					projectId: graph.projectId,
					projectName: projMap[graph.projectId]?.name,
					name: eq.name,
					alert: isAlert,
					warn: isWarn,
					lastAlertDate,
					lastWarnDate,
					state: alertEntry?.state,
					sendCase: alertEntry?.sendCase,
				})
			}
		}

		result.sort((a, b) => {
			if (a.alert !== b.alert) return a.alert ? -1 : 1
			if (a.warn !== b.warn) return a.warn ? -1 : 1
			const alertDiff = (b.lastAlertDate || 0) - (a.lastAlertDate || 0)
			if (alertDiff !== 0) return alertDiff
			return (b.lastWarnDate || 0) - (a.lastWarnDate || 0)
		})

		return result
	})

	// All equations with an alarm/warning configured, with their *current* breach
	// status taken from the same live maps as sensorAlerts (alerts/warns) — NOT
	// from the persisted alertData.state, which stays 'alert' long after a breach
	// cleared. So a configured-but-currently-OK alarm correctly shows as OK here.
	const configuredAlerts = computed<SensorAlertRow[]>(() => {
		// Only the currently-viewed graph's alarms — the widget is shown per graph.
		const activeGraphId = baseStore.getActiveIds.graphId
		if (!activeGraphId) return []
		const eqs = flattenedEquations.value
		const alertsMap = alerts.value || {}
		const warnsMap = warns.value || {}
		const locMap = locationsMap.value
		const projMap = projectMap.value
		const rows: SensorAlertRow[] = []
		for (const key of Object.keys(eqs)) {
			const fe = eqs[key]
			if (fe.graphId !== activeGraphId) continue
			if (!fe.isOnAlert && !fe.isOnWarn) continue
			const skey = `-/-/${fe.equationName}`
			const alertEntry = alertsMap[fe.graphId]
			const warnEntry = warnsMap[fe.graphId]
			const isAlert = Boolean(alertEntry?.data?.[skey])
			const isWarn = Boolean(warnEntry?.data?.[skey])
			rows.push({
				key,
				cgId: fe.graphId,
				cgName: fe.graphName,
				locationId: fe.locationId,
				locationName: locMap[fe.locationId]?.name,
				projectId: fe.projectId,
				projectName: projMap[fe.projectId]?.name,
				name: fe.equationName,
				alert: isAlert,
				warn: isWarn,
				lastAlertDate: alertEntry?.alertDates?.[skey],
				lastWarnDate: warnEntry?.warnDates?.[skey],
				state: isAlert ? 'alert' : isWarn ? 'warn' : 'ok',
			})
		}
		// Alarm first, then warning, then OK; within a level alphabetic by graph.
		rows.sort((a, b) => {
			if (a.alert !== b.alert) return a.alert ? -1 : 1
			if (a.warn !== b.warn) return a.warn ? -1 : 1
			return (a.cgName || '').localeCompare(b.cgName || '')
		})
		return rows
	})

	const hierarchy = computed<AlertsHierarchyNode[]>(() => {
		const projMap = projectMap.value
		const locByProject = locationsByProjectId.value
		const hubsByLocation = hubsByLoc.value
		const modulesByHub = modulesByHubSerial.value
		const comappsByLocation = comappsByLId.value

		return projects.value.map((project) => {
			const projectId = project._id
			const projectState = allStates.value?.projects?.[projectId] || 'ok'
			const projectNode: AlertsHierarchyNode = {
				id: projectId,
				type: 'project',
				name: makeProjectOptionLabel(project),
				state: projectState,
				meta: projMap[projectId],
				children: [],
			}

			const locationsForProject = locByProject[projectId] || []
			projectNode.children = locationsForProject.map((location: any) => {
				const locationId = location._id
				const locationState = allStates.value?.locations?.[locationId] || 'ok'
				const locationNode: AlertsHierarchyNode = {
					id: locationId,
					type: 'location',
					name: location.name,
					state: locationState,
					meta: location,
					children: [],
				}

				const hubsForLocation = hubsByLocation[locationId] || []
				const hubNodes = hubsForLocation.map((hub: any) => {
					const hubId = hub._id || hub.serial
					const hubState = allStates.value?.hubs?.[hub._id] || allStates.value?.hubs?.[hub.serial] || 'ok'
					const hubNode: AlertsHierarchyNode = {
						id: hubId,
						type: 'hub',
						name: hub.name || hub.serial,
						state: hubState,
						meta: hub,
						children: [],
					}

					const moduleList = modulesByHub[hub.serial] || []
					const moduleNodes = moduleList.map((mod) => {
						const moduleState = allStates.value?.modules?.[mod._id] || 'ok'
						return {
							id: mod._id,
							type: 'module' as const,
							name: mod.customName || mod.name || mod._id,
							state: moduleState,
							meta: mod,
						}
					})

					hubNode.children = moduleNodes
					return hubNode
				})

				const comappNodes = (comappsByLocation[locationId] || []).map((comapp: any) => {
					const state = allStates.value?.comapps?.[comapp._id] || 'ok'
					return {
						id: comapp._id,
						type: 'comapp' as const,
						name: comapp.pcName || comapp._id,
						state,
						meta: comapp,
					}
				})

				locationNode.children = [...hubNodes, ...comappNodes]
				return locationNode
			})

			return projectNode
		})
	})

	const statusBadge = (state: string | null | undefined) => {
		const normalized = (state || '').toLowerCase()
		switch (normalized) {
			case 'alert':
			case 'error':
			case 'offline':
				return 'badge badge-error'
			case 'warn':
			case 'prewarn':
			case 'warnprealert':
			case 'prealert':
				return 'badge badge-warning'
			case 'pending':
				return 'badge badge-info'
			case 'ok':
			case 'online':
				return 'badge badge-success'
			default:
				return 'badge badge-ghost'
		}
	}

	async function ensureModulesLoaded(force = false) {
		if (force || modules.value.length === 0) {
			await moduleStore.listAllModules()
		}
	}

	async function init(options: InitOptions = {}) {
		const { force = false, includeStates = false } = options

		if (force && initInFlight) {
			await initInFlight
		}

		if (!force && initInFlight) {
			await initInFlight
			if (includeStates && !statesLoadedOnce) {
				await baseStore.fetchStates()
				statesLoadedOnce = hasStatePayload(allStates.value) || statesLoadedOnce
			}
			return
		}

		const runner = (async () => {
			const tasks: Promise<any>[] = []

			if (force || !alertsStore.hasLoadedGraphs) tasks.push(alertsStore.fetchCustomGraphs())
			if (force || !alertsStore.hasLoadedAlerts) tasks.push(alertsStore.fetchAlerts())
			if (force || users.value.length === 0) tasks.push(usersStore.fetchUsers())
			tasks.push(ensureModulesLoaded(force))
			if (includeStates && (force || !statesLoadedOnce)) {
				tasks.push(
					baseStore.fetchStates().then(() => {
						statesLoadedOnce = true
					}),
				)
			}

			await Promise.allSettled(tasks)
			if (!statesLoadedOnce) {
				statesLoadedOnce = hasStatePayload(allStates.value)
			}
		})()

		if (force) {
			await runner
			return
		}

		initInFlight = runner
		try {
			await runner
		} finally {
			initInFlight = null
		}
	}

	function createUserMatrixContext(): UserMatrixContext {
		const selectedProjectIds = ref<string[]>([])
		const hasWarnAlerts = ref(true)
		const warnSelectAll = ref<Record<string, boolean>>({})
		const alertSelectAll = ref<Record<string, boolean>>({})

		const userAlertData = ref<AlertMatrixSelection>({})
		const currentUserAlertData = ref<AlertMatrixSelection>({})
		const alertMetaData = ref<AlertMetaSelection>({})
		const currentUserAlertMetaData = ref<AlertMetaSelection>({})

		const accessUserGraph = ref<Record<string, Record<string, boolean>>>({})

		const filteredEquations = computed(() => {
			const includeAlertsOnly = hasWarnAlerts.value
			const selected = selectedProjectIds.value
			const selectedProject = selected[0]
			const result: Record<string, FlattenedEquation> = {}
			for (const [key, entry] of Object.entries(flattenedEquations.value)) {
				if (includeAlertsOnly && !(entry.isOnWarn || entry.isOnAlert)) continue
				if (selected.length) {
					if (selectedProject === 'general') {
						if (entry.projectId !== 'general') continue
					} else if (entry.projectId !== selectedProject) {
						continue
					}
				}
				result[key] = entry
			}
			return result
		})

		const diffs = computed(() => diff(currentUserAlertData.value, userAlertData.value))
		const diffsMeta = computed(() => diff(currentUserAlertMetaData.value, alertMetaData.value))
		const hasChanges = computed(() => diffs.value.isChanged || diffsMeta.value.isChanged)

		function resetSelectAll() {
			warnSelectAll.value = {}
			alertSelectAll.value = {}
		}

		watch(
			() => Object.keys(filteredEquations.value),
			() => resetSelectAll(),
		)

		function prepareAlertData() {
			if (!users.value.length) {
				userAlertData.value = {}
				currentUserAlertData.value = {}
				alertMetaData.value = {}
				currentUserAlertMetaData.value = {}
				return
			}
			const alertData: Record<string, any> = {}
			const metaData: Record<string, any> = {}
			for (const user of users.value) {
				alertData[user._id] = user.alertData || {}
				metaData[user._id] = user.alertMetaData || {}
			}
			currentUserAlertData.value = dotify(alertData)
			currentUserAlertMetaData.value = dotify(metaData)
			userAlertData.value = dotify(alertData)
			alertMetaData.value = dotify(metaData)
		}

		function computeAccessMatrix() {
			const matrix: Record<string, Record<string, boolean>> = {}
			const serialsByProject = graphSerialsByProject.value
			const allSerials = allGraphSerials.value

			for (const user of users.value) {
				const userAccess: Record<string, boolean> = {}
				if (ADMIN_ROLES.has(user.role)) {
					for (const serial of allSerials) {
						userAccess[serial] = true
					}
				} else {
					const accessProjects: string[] = Array.isArray(user.accessProjects) ? user.accessProjects : []
					for (const projectId of accessProjects) {
						const serials = serialsByProject[projectId]
						if (!serials) continue
						for (const serial of serials) {
							userAccess[serial] = true
						}
					}
				}
				matrix[user._id] = userAccess
			}
			accessUserGraph.value = matrix
		}

		function syncFromStores() {
			prepareAlertData()
			computeAccessMatrix()
			resetSelectAll()
		}

		function hasAccessToEquation(userId: string, graphSerial: string) {
			return Boolean(accessUserGraph.value[userId]?.[graphSerial])
		}

		function hasUserAccessToProject(user: any) {
			if (ADMIN_ROLES.has(user.role)) return true
			if (!selectedProjectIds.value.length) return true
			const selected = selectedProjectIds.value[0]
			if (selected === 'general') return true
			const projects: string[] = Array.isArray(user.accessProjects) ? user.accessProjects : []
			return projects.includes(selected)
		}

		function setSelectAll(type: 'warn' | 'alert', key: string, value: boolean) {
			const store = type === 'warn' ? warnSelectAll.value : alertSelectAll.value
			if (value) store[key] = true
			else delete store[key]

			const equation = filteredEquations.value[key]
			if (!equation) return
			for (const user of users.value) {
				if (!hasAccessToEquation(user._id, equation.graphSerial)) continue
				const dataKey = `${user._id}.${key}.${type}`
				userAlertData.value[dataKey] = value
			}
		}

		return {
			selectedProjectIds,
			hasWarnAlerts,
			warnSelectAll,
			alertSelectAll,
			userAlertData,
			currentUserAlertData,
			alertMetaData,
			currentUserAlertMetaData,
			filteredEquations,
			hasChanges,
			diffs,
			diffsMeta,
			prepareAlertData,
			computeAccessMatrix,
			syncFromStores,
			setSelectAll,
			hasAccessToEquation: (userId, graphSerial, _projectId) => hasAccessToEquation(userId, graphSerial),
			hasUserAccessToProject,
			resetSelectAll,
		}
	}

	function createGraphMatrixContext(): GraphMatrixContext {
		const includeWarnAlerts = ref(true)
		const selectedProjectId = ref('')
		const equationsByProject = computed(() => {
			const map: Record<string, FlattenedEquation[]> = {}
			const projectFilter = selectedProjectId.value
			for (const entry of Object.values(flattenedEquations.value)) {
				if (includeWarnAlerts.value && !(entry.isOnWarn || entry.isOnAlert)) continue
				if (projectFilter && entry.projectId !== projectFilter) continue
				if (!map[entry.projectId]) map[entry.projectId] = []
				map[entry.projectId].push(entry)
			}
			for (const group of Object.values(map)) {
				group.sort((a, b) => {
					const graphCompare = (a.graphName || '').localeCompare(b.graphName || '')
					if (graphCompare !== 0) return graphCompare
					return (a.equationName || '').localeCompare(b.equationName || '')
				})
			}
			return map
		})

		return {
			includeWarnAlerts,
			selectedProjectId,
			equationsByProject,
			graphAlertDataByEquationKey,
		}
	}

	return {
		// stores
		alertsStore,
		usersStore,
		projectStore,
		locationStore,
		hubStore,
		moduleStore,
		comappStore,
		baseStore,
		// shared refs
		customGraphs,
		alerts,
		warns,
		users,
		projects,
		projectMap,
		locationsMap,
		modules,
		allStates,
		// derived
		projectOptions,
		flattenedEquations,
		graphAlertDataByEquationKey,
		sensorAlerts,
		configuredAlerts,
		hierarchy,
		statusBadge,
		combinedLoading,
		// actions
		init,
		createUserMatrixContext,
		createGraphMatrixContext,
		updateAlertData: usersStore.updateAlertData,
		analyzeAlertData: usersStore.analyzeAlertData,
		sendWarnAlertEmailTrigger: usersStore.sendWarnAlertEmailTrigger,
		fetchUsers: usersStore.fetchUsers,
	}
}
